import { useMemo } from "react";

interface SnowflakeProps {
  style: React.CSSProperties;
  size: "sm" | "md" | "lg";
}

function Snowflake({ style, size }: SnowflakeProps) {
  const sizeClasses = {
    sm: "w-1 h-1",
    md: "w-1.5 h-1.5",
    lg: "w-2 h-2",
  };

  return (
    <div
      className={`absolute rounded-full bg-white opacity-80 ${sizeClasses[size]}`}
      style={style}
    />
  );
}

interface SnowfallProps {
  count?: number;
  className?: string;
}

export function Snowfall({ count = 20, className = "" }: SnowfallProps) {
  const snowflakes = useMemo(() => {
    return Array.from({ length: count }, (_, i) => {
      const size = (["sm", "md", "lg"] as const)[Math.floor(Math.random() * 3)];
      const left = Math.random() * 100;
      const animationDuration = 4 + Math.random() * 4;
      // Negative delay starts animation partway through, distributing snowflakes on load
      const animationDelay = -Math.random() * animationDuration;
      const opacity = 0.4 + Math.random() * 0.6;
      const drift = -20 + Math.random() * 40;

      return {
        id: i,
        size,
        style: {
          left: `${left}%`,
          opacity,
          animation: `snowfall ${animationDuration}s linear ${animationDelay}s infinite`,
          "--drift": `${drift}px`,
        } as React.CSSProperties,
      };
    });
  }, [count]);

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      <style>
        {`
          @keyframes snowfall {
            0% {
              transform: translateY(-10px) translateX(0px) rotate(0deg);
              opacity: 0;
            }
            10% {
              opacity: 0.8;
            }
            90% {
              opacity: 0.6;
            }
            100% {
              transform: translateY(160px) translateX(var(--drift)) rotate(360deg);
              opacity: 0;
            }
          }
        `}
      </style>
      {snowflakes.map((flake) => (
        <Snowflake key={flake.id} size={flake.size} style={flake.style} />
      ))}
    </div>
  );
}
